import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import debounce from 'debounce';
import { gql, useApolloClient } from '@apollo/client';

import Link from '../Link';
import Icon from '../Icon/Icon';

import CollapsibleItem from './CollapsibleItem/CollapsibleItem';
import configs from './configs';

const GET_SEARCH_RESULTS = gql`
  query getSearchResults($query: String) {
    search {
      find(query: $query)
    }
  }
`;

const Search = ({ onShowSearchResults, siteId }) => {
  const client = useApolloClient();

  const [isShowDropdown, setIsShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [results, setResults] = useState(null);

  const fetchResults = async () => {
    setIsLoading(true);
    client
      .query({
        query: GET_SEARCH_RESULTS,
        variables: {
          query: searchValue,
        },
      })
      .then(searchResults => {
        setTypeFilter('');
        setResults(
          JSON.parse(
            (((searchResults || {}).data || {}).search || {}).find || {},
          ),
        );
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const debouncedFetchResults = debounce(fetchResults, 500);

  useEffect(() => {
    if (searchValue.length >= 2) {
      debouncedFetchResults();
    }
  }, [searchValue]);

  const handleShowDropdown = () => {
    setIsShowDropdown(true);
    if (onShowSearchResults) onShowSearchResults(true);
  };

  const handleHideDropdown = () => {
    setIsShowDropdown(false);
    if (onShowSearchResults) onShowSearchResults(false);
  };

  const handleChangeValue = e => {
    // setResults(null);
    setSearchValue(e.target.value);
  };

  const handleClearValue = () => {
    setSearchValue('');
  };

  const handleChangeTypeFilter = e => {
    setTypeFilter(e.target.value);
  };

  const noResultsString = `${searchValue.substring(0, 20)}${
    searchValue.length > 20 ? '...' : ''
  }`;

  const totalResultsCount = Object.keys(results || {}).flatMap(
    resultKey => results[resultKey].docs,
  ).length;

  return (
    <form className="Search">
      {isShowDropdown && (
        <div className="Search__overlay" onClick={handleHideDropdown} />
      )}
      {isShowDropdown && (
        <div className="Search__back" onClick={handleHideDropdown}>
          <Icon icon="chevronRight" />
          <span>Menu</span>
        </div>
      )}

      <label
        className={cx('Search__wrapper', {
          '--isActive': isShowDropdown,
        })}
        htmlFor="search"
      >
        <div className="Search__searchIcon">
          <Icon icon="search" />
        </div>
        <input
          className="Search__searchInput"
          type="text"
          id="search"
          placeholder="Search"
          autoComplete="off"
          value={searchValue}
          onChange={handleChangeValue}
          onFocus={handleShowDropdown}
          maxLength="50"
        />
        {searchValue.length > 0 && (
          <button
            className="Search__clear"
            type="button"
            onClick={handleClearValue}
          >
            <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
              <path
                fill="#fff"
                d="M 9.038 8.297 L 9.038 7.59 L 11.408 5.22 L 10.729 4.541 L 8.357 6.913 L 7.65 6.913 L 5.28 4.543 L 4.599 5.224 L 6.993 7.618 L 6.993 8.277 L 4.601 10.669 L 5.282 11.35 L 7.676 8.956 L 8.335 8.956 L 10.729 11.35 L 11.41 10.669 L 9.038 8.297 Z"
              />
            </svg>
          </button>
        )}
      </label>

      <div className="Search__dropdown">
        <div className="Search__results">
          {isShowDropdown ? (
            <div className="Search__resultsContainer">
              {isLoading && (
                <div className="Search__info">
                  <div className="Search__infoIconContainer">
                    <Icon icon="search" />
                  </div>
                  <span>Loading results...</span>
                </div>
              )}

              {!isLoading && results == null && (
                <div className="Search__info">
                  <div className="Search__infoIconContainer">
                    <Icon icon="search" />
                  </div>
                  <span>Start a Unified Search…</span>
                </div>
              )}

              {!isLoading && results != null && totalResultsCount === 0 && (
                <div className="Search__info">
                  <div className="Search__noResults">
                    No results for: &apos;{noResultsString}&apos;
                  </div>
                  <div>
                    <strong>Search tips:</strong> Some search terms require an
                    exact match. Try typing the entire term, or use a different
                    word or phrase.
                  </div>
                </div>
              )}

              {!isLoading && totalResultsCount > 0 && (
                <div className="Search__resultsHeader">
                  <div className="Search__resultsTitle">
                    {totalResultsCount.toLocaleString()} Results
                  </div>
                  <div className="Search__resultsFilter">
                    <span
                      style={{
                        color: '#8f95a5',
                      }}
                    >
                      Filter:
                    </span>
                    <select
                      style={{
                        marginLeft: 4,
                        color: 'inherit',
                        backgroundColor: 'transparent',
                        font: 'inherit',
                        border: 'none',
                      }}
                      onChange={handleChangeTypeFilter}
                      value={typeFilter}
                    >
                      <option key="" value="">
                        Everything
                      </option>
                      {Object.keys(results)
                        .filter(
                          resultKey =>
                            configs[resultKey] != null &&
                            results[resultKey].docs.length > 0,
                        )
                        .map(resultKey => {
                          const config = configs[resultKey];
                          return (
                            <option key={resultKey} value={resultKey}>
                              Only {config.label}
                            </option>
                          );
                        })}
                    </select>
                  </div>
                </div>
              )}

              {!isLoading && totalResultsCount > 0 && (
                <div className="Search__scrollable">
                  {/* RESULTS LIST */}
                  {Object.keys(results)
                    .filter(
                      resultKey =>
                        configs[resultKey] != null &&
                        results[resultKey].docs.length > 0,
                    )
                    .filter(resultKey => {
                      if (typeFilter === '') return true;
                      return typeFilter === resultKey;
                    })
                    .map(resultKey => {
                      const config = configs[resultKey];
                      return (
                        <ul key={resultKey} className="Search__list">
                          <li className="Search__listTitle">{config.label}</li>
                          {results[resultKey].docs.map(doc => {
                            const actions = config.actions({ siteId, doc });
                            return (
                              <Fragment key={doc._id}>
                                <li className="Search__listItem">
                                  <Link
                                    className="Search__listLink"
                                    to={actions[0].link}
                                    onClick={handleHideDropdown}
                                  >
                                    <span>{doc.n}</span>
                                  </Link>
                                  <div className="Search__listItemBreadcrumbs">
                                    {actions.map(action => (
                                      <Link
                                        key={action.id}
                                        className="Search__listItemBreadcrumb"
                                        to={action.link}
                                        onClick={handleHideDropdown}
                                      >
                                        <span>{action.label}</span>
                                        <Icon icon="chevronRight" />
                                      </Link>
                                    ))}
                                  </div>
                                </li>
                                <CollapsibleItem label={doc.n}>
                                  <div className="Search__listItemBreadcrumbs --isVisible">
                                    {actions.map(action => (
                                      <Link
                                        key={action.id}
                                        className="Search__listItemBreadcrumb"
                                        to={action.link}
                                        onClick={handleHideDropdown}
                                      >
                                        <span>{action.label}</span>
                                        <Icon icon="chevronRight" />
                                      </Link>
                                    ))}
                                  </div>
                                </CollapsibleItem>
                              </Fragment>
                            );
                          })}
                        </ul>
                      );
                    })}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </form>
  );
};

Search.propTypes = {
  onShowSearchResults: PropTypes.func,
  siteId: PropTypes.string.isRequired,
};

Search.defaultProps = {
  onShowSearchResults: null,
};

export default Search;



// WEBPACK FOOTER //
// ./src/components/Search/Search.js